import { recordView } from './views';
import { isUserAuthenticated, userDataService } from './services/userDataService';
import { DEFAULT_COUNCIL_SLUG, normalizeCouncilSlug } from '@/lib/councils';

// Centralized view tracking helper
const SESSION_KEY_PREFIX = 'cs_viewed_';
const COOLDOWN_DURATION = 60 * 1000;

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    mobile?: boolean;
  };
};

type CityScopeDebug = {
  viewTracker?: {
    trackView: typeof trackViewWithCooldown;
    clearCooldowns: typeof clearViewCooldowns;
    getRemainingCooldown: typeof getRemainingCooldown;
    getCooldownDuration: () => number;
    listActiveCooldowns: () => Record<string, number>;
  };
  [key: string]: unknown;
};

declare global {
  interface Window {
    cityScope?: CityScopeDebug;
  }
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

function getCooldownSessionKey(referenceNo: string, council?: string): string {
  const normalizedCouncil = normalizeCouncilSlug(council) || DEFAULT_COUNCIL_SLUG;
  const sanitizedRef = referenceNo.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${SESSION_KEY_PREFIX}${normalizedCouncil}_${sanitizedRef}`;
}

function getRemainingCooldownForKey(sessionKey: string): number {
  const lastViewedStr = sessionStorage.getItem(sessionKey);
  if (!lastViewedStr) return 0;

  const lastViewed = parseInt(lastViewedStr, 10);
  if (isNaN(lastViewed)) return 0;

  return Math.max(0, COOLDOWN_DURATION - (Date.now() - lastViewed));
}

/**
 * Tracks a view and mirrors it to the local cache for instant UI updates
 * @param referenceNo - The planning application reference number
 * @param context - Additional context about the view
 * @returns Promise<boolean> - true if view was recorded, indicates whether the view was accepted for server sync
 */
export async function trackViewWithCooldown(
  referenceNo: string,
  context?: {
    source?: 'map' | 'search' | 'application' | 'direct';
    path?: string;
    device?: 'mobile' | 'desktop';
    timestamp?: string;
    category?: string;
    address?: string;
    scale_rating?: number;
    council?: string;
  }
): Promise<boolean> {
  if (!referenceNo || typeof referenceNo !== 'string') {
    console.warn('Invalid reference number for view tracking:', referenceNo);
    return false;
  }

  const council = normalizeCouncilSlug(context?.council) || DEFAULT_COUNCIL_SLUG;
  const sessionKey = getCooldownSessionKey(referenceNo, council);
  const now = Date.now();

  const remainingCooldown = getRemainingCooldown(referenceNo, council);
  if (remainingCooldown > 0) {
    return true;
  }

  try {
    sessionStorage.setItem(sessionKey, now.toString());
  } catch {
    // Ignore storage errors.
  }

  const trackingContext = {
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    source: context?.source ?? 'direct',
    device: detectDevice(),
    timestamp: new Date().toISOString(),
    ...context,
    council,
  };

  try {
    await userDataService.addView({
      reference_no: referenceNo,
      address: trackingContext.address || '',
      category: trackingContext.category,
      scale_rating: trackingContext.scale_rating,
      source_council: council,
    });
  } catch (cacheError) {
    console.warn('Failed to update local view cache:', cacheError);
  }

  if (!(await isUserAuthenticated())) {
    console.log(`View tracked locally for ${referenceNo} from ${trackingContext.source}`);
    return true;
  }

  recordView(referenceNo, trackingContext)
    .then(() => {
      userDataService.markViewSynced(referenceNo, trackingContext.council);
    })
    .catch((error: unknown) => {
      userDataService.markViewSyncFailure(referenceNo, error, trackingContext.council);
      if (getErrorStatus(error) === 401) {
        console.log(`View tracking failed for ${referenceNo} - authentication expired`);
      } else {
        console.warn('Failed to record view:', error);
      }
    });

  console.log(`View tracked for ${referenceNo} from ${trackingContext.source}`);
  return true;
}

/**
 * Detects device type based on user agent
 */
function detectDevice(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';

  const navigatorWithUserAgentData = window.navigator as NavigatorWithUserAgentData;
  if (
    navigatorWithUserAgentData.userAgentData &&
    typeof navigatorWithUserAgentData.userAgentData.mobile === 'boolean'
  ) {
    return navigatorWithUserAgentData.userAgentData.mobile ? 'mobile' : 'desktop';
  }

  return /Mobile|Android|iPhone|iPad/i.test(window.navigator.userAgent) ? 'mobile' : 'desktop';
}

/**
 * Detects source based on current route
 */
export function detectSource(): 'map' | 'search' | 'application' | 'direct' {
  if (typeof window === 'undefined') return 'direct';
  
  const path = window.location.pathname;
  if (path.includes('planning-map')) return 'map';
  if (path.includes('planning-updates')) return 'search';
  if (path.includes('application')) return 'application';
  if (document.referrer && document.referrer.includes('planning-')) return 'search';
  
  return 'direct';
}

/**
 * Clears all view tracking cooldowns (useful for testing)
 */
export function clearViewCooldowns(): void {
  if (typeof window === 'undefined') return;
  
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(SESSION_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => sessionStorage.removeItem(key));
  console.log(`Cleared ${keysToRemove.length} view tracking cooldowns`);
}

/**
 * Gets remaining cooldown time for a reference number
 */
export function getRemainingCooldown(referenceNo: string, council?: string): number {
  if (!referenceNo || typeof window === 'undefined') return 0;

  return getRemainingCooldownForKey(getCooldownSessionKey(referenceNo, council));
}

// Developer utilities - available in development mode
const VIEW_TRACKER_HELP_FLAG = '__cityScopeViewTrackerDevHelpLogged';
if (
  typeof window !== 'undefined' &&
  process.env.NODE_ENV === 'development'
) {
  const existingCityScope = window.cityScope ?? {};
  window.cityScope = {
    ...existingCityScope,
    viewTracker: {
      trackView: trackViewWithCooldown,
      clearCooldowns: clearViewCooldowns,
      getRemainingCooldown,
      getCooldownDuration: () => COOLDOWN_DURATION,
      listActiveCooldowns: () => {
        const cooldowns: { [key: string]: number } = {};
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(SESSION_KEY_PREFIX)) {
            const ref = key.replace(SESSION_KEY_PREFIX, '');
            const remaining = getRemainingCooldownForKey(key);
            if (remaining > 0) {
              cooldowns[ref] = Math.ceil(remaining / 1000); // Show in seconds
            }
          }
        }
        return cooldowns;
      }
    }
  };

  const flagged = window as unknown as Record<string, boolean>;
  if (!flagged[VIEW_TRACKER_HELP_FLAG]) {
    flagged[VIEW_TRACKER_HELP_FLAG] = true;
    console.debug(
      'cityScope.viewTracker: trackView, clearCooldowns, getRemainingCooldown, listActiveCooldowns (dev)'
    );
  }
}
